import { z } from 'zod';
import { moneySchema, accountTypeSchema } from './schemas.js';

export const IMPORT_TYPES = [
  'coa',
  'opening_balances',
  'customers',
  'vendors',
  'tax_definitions'
] as const;

export type ImportType = (typeof IMPORT_TYPES)[number];

export type ImportFieldDef = {
  key: string;
  required: boolean;
  labelKey: string;
};

export const IMPORT_FIELD_DEFS: Record<ImportType, ImportFieldDef[]> = {
  coa: [
    { key: 'code', required: true, labelKey: 'import.fields.code' },
    { key: 'name', required: true, labelKey: 'import.fields.name' },
    { key: 'nameAr', required: false, labelKey: 'import.fields.nameAr' },
    { key: 'type', required: true, labelKey: 'import.fields.type' }
  ],
  opening_balances: [
    { key: 'accountCode', required: true, labelKey: 'import.fields.accountCode' },
    { key: 'debit', required: false, labelKey: 'import.fields.debit' },
    { key: 'credit', required: false, labelKey: 'import.fields.credit' },
    { key: 'idempotencyKey', required: false, labelKey: 'import.fields.idempotencyKey' }
  ],
  customers: [
    { key: 'code', required: true, labelKey: 'import.fields.code' },
    { key: 'name', required: true, labelKey: 'import.fields.name' },
    { key: 'nameAr', required: false, labelKey: 'import.fields.nameAr' },
    { key: 'email', required: false, labelKey: 'import.fields.email' },
    { key: 'phone', required: false, labelKey: 'import.fields.phone' },
    { key: 'idempotencyKey', required: false, labelKey: 'import.fields.idempotencyKey' }
  ],
  vendors: [
    { key: 'code', required: true, labelKey: 'import.fields.code' },
    { key: 'name', required: true, labelKey: 'import.fields.name' },
    { key: 'nameAr', required: false, labelKey: 'import.fields.nameAr' },
    { key: 'email', required: false, labelKey: 'import.fields.email' },
    { key: 'idempotencyKey', required: false, labelKey: 'import.fields.idempotencyKey' }
  ],
  tax_definitions: [
    { key: 'name', required: true, labelKey: 'import.fields.name' },
    { key: 'nameAr', required: false, labelKey: 'import.fields.nameAr' },
    { key: 'ratePercent', required: false, labelKey: 'import.fields.ratePercent' },
    { key: 'fixedAmount', required: false, labelKey: 'import.fields.fixedAmount' },
    { key: 'isInclusive', required: false, labelKey: 'import.fields.isInclusive' },
    { key: 'idempotencyKey', required: false, labelKey: 'import.fields.idempotencyKey' }
  ]
};

const coaRowSchema = z.object({
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(120),
  nameAr: z.string().max(120).optional(),
  type: accountTypeSchema
});

const openingRowSchema = z.object({
  accountCode: z.string().min(1),
  debit: moneySchema.optional().default('0.00'),
  credit: moneySchema.optional().default('0.00'),
  idempotencyKey: z.string().max(120).optional()
});

const partyRowSchema = z.object({
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(120),
  nameAr: z.string().max(120).optional(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().max(40).optional(),
  idempotencyKey: z.string().max(120).optional()
});

const vendorRowSchema = partyRowSchema.omit({ phone: true });

const taxRowSchema = z.object({
  name: z.string().min(1).max(120),
  nameAr: z.string().max(120).optional(),
  ratePercent: z.coerce.number().min(0).max(100).optional(),
  fixedAmount: moneySchema.optional(),
  isInclusive: z
    .union([z.boolean(), z.string()])
    .optional()
    .transform((v) => {
      if (v === undefined) return false;
      if (typeof v === 'boolean') return v;
      return ['true', '1', 'yes', 'y'].includes(v.toLowerCase());
    }),
  idempotencyKey: z.string().max(120).optional()
});

export type ImportRowError = {
  rowIndex: number;
  field?: string;
  message: string;
};

export type ValidatedImportRow<T = Record<string, unknown>> = {
  rowIndex: number;
  data: T;
  idempotencyKey: string;
};

function schemaForType(type: ImportType): z.ZodType<Record<string, unknown>> {
  switch (type) {
    case 'coa':
      return coaRowSchema;
    case 'opening_balances':
      return openingRowSchema;
    case 'customers':
      return partyRowSchema;
    case 'vendors':
      return vendorRowSchema;
    case 'tax_definitions':
      return taxRowSchema;
    default:
      throw new Error(`Unknown import type: ${type satisfies never}`);
  }
}

export function computeIdempotencyKey(
  importType: ImportType,
  rowIndex: number,
  data: Record<string, unknown>
): string {
  const explicit = data.idempotencyKey;
  if (typeof explicit === 'string' && explicit.trim()) {
    return `${importType}:${explicit.trim()}`;
  }
  if (importType === 'coa' && data.code) return `${importType}:${String(data.code)}`;
  if (importType === 'opening_balances' && data.accountCode) {
    return `${importType}:${String(data.accountCode)}`;
  }
  if ((importType === 'customers' || importType === 'vendors') && data.code) {
    return `${importType}:${String(data.code)}`;
  }
  if (importType === 'tax_definitions' && data.name) {
    return `${importType}:${String(data.name)}:${String(data.ratePercent ?? '')}`;
  }
  return `${importType}:row:${rowIndex}`;
}

export function validateImportRows(
  importType: ImportType,
  rawRows: Record<string, string>[]
): { valid: ValidatedImportRow[]; errors: ImportRowError[] } {
  const schema = schemaForType(importType);
  const valid: ValidatedImportRow[] = [];
  const errors: ImportRowError[] = [];

  rawRows.forEach((raw, rowIndex) => {
    const cleaned: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (v !== undefined && v !== null) {
        const s = String(v).trim();
        if (s !== '') cleaned[k] = s;
      }
    }
    if (importType === 'opening_balances') {
      if (!cleaned.debit) cleaned.debit = '0.00';
      if (!cleaned.credit) cleaned.credit = '0.00';
    }
    const parsed = schema.safeParse(cleaned);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        errors.push({
          rowIndex,
          field: issue.path.join('.'),
          message: issue.message
        });
      }
      return;
    }
    const data = parsed.data as Record<string, unknown>;
    valid.push({
      rowIndex,
      data,
      idempotencyKey: computeIdempotencyKey(importType, rowIndex, data)
    });
  });

  return { valid, errors };
}

export function applyColumnMapping(
  headers: string[],
  rows: string[][],
  mapping: Record<string, string>
): Record<string, string>[] {
  const headerIndex = new Map(headers.map((h, i) => [h, i]));
  return rows.map((row) => {
    const out: Record<string, string> = {};
    for (const [field, sourceCol] of Object.entries(mapping)) {
      if (!sourceCol) continue;
      const idx = headerIndex.get(sourceCol);
      if (idx === undefined) continue;
      out[field] = row[idx] ?? '';
    }
    return out;
  });
}

export function errorsToCsv(errors: ImportRowError[]): string {
  const lines = ['row,field,message'];
  for (const e of errors) {
    const field = e.field ?? '';
    const msg = e.message.replace(/"/g, '""');
    lines.push(`${e.rowIndex + 1},"${field}","${msg}"`);
  }
  return lines.join('\n');
}
